import React, { useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const categories = [
  'Productivity',
  'Creative',
  'Coding',
  'Writing',
  'Research',
  'Marketing',
  'Education',
  'Entertainment',
  'Business',
  'Health'
];

export const DealSubscriptionForm: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [frequency, setFrequency] = useState('weekly');

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('deal_subscriptions')
        .upsert({
          user_id: user?.id || null,
          email,
          categories: selectedCategories,
          frequency,
          is_active: true
        }, {
          onConflict: 'email'
        });

      if (error) throw error;

      toast.success('Subscribed to deal alerts!', {
        description: `You'll receive ${frequency} updates about AI deals.`
      });
      setIsOpen(false);
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error('Failed to subscribe', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover-lift">
          <Bell className="w-4 h-4 mr-2" />
          Get Deal Alerts
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Subscribe to Deal Alerts
          </DialogTitle>
          <DialogDescription>
            Get notified when new deals drop on your favorite AI tools.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Alert Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">Instant (as deals drop)</SelectItem>
                <SelectItem value="daily">Daily Digest</SelectItem>
                <SelectItem value="weekly">Weekly Roundup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Categories (optional)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Select specific categories or leave empty for all deals
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1">
              {categories.map(category => (
                <label
                  key={category}
                  className="flex items-center gap-2 p-2 rounded-lg border border-border hover:bg-muted cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={selectedCategories.includes(category)}
                    onCheckedChange={() => handleCategoryToggle(category)}
                  />
                  <span className="text-sm">{category}</span>
                </label>
              ))}
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Subscribing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Subscribe to Alerts
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
